import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Templates from "@/pages/Templates";
import Builder from "@/pages/Builder";
import DeliveryEditor from "@/pages/DeliveryEditor";
import Leads from "@/pages/Leads";
import Integrations from "@/pages/Integrations";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import Analytics from "@/pages/Analytics";
import Marketplace from "@/pages/Marketplace";
import PublicLanding from "@/pages/PublicLanding";
import PublicDelivery from "@/pages/PublicDelivery";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Confirm from "@/pages/Confirm";

import "@/App.css";

function ProtectedRoute({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="card-brutal px-8 py-6 font-display text-xl">Loading…</div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection of session_id fragment to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/pricing" element={<Pricing />} />

      {/* Public landing pages (no auth) */}
      <Route path="/lm/:slug" element={<PublicLanding />} />
      <Route path="/lm/:slug/delivery" element={<PublicDelivery />} />
      <Route path="/p/:customPath" element={<PublicLanding byPath />} />
      <Route path="/p/:customPath/delivery" element={<PublicDelivery byPath />} />
      <Route path="/confirm/:token" element={<Confirm />} />

      {/* Protected user routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
      <Route path="/builder/:id" element={<ProtectedRoute><Builder /></ProtectedRoute>} />
      <Route path="/delivery-editor/:id" element={<ProtectedRoute><DeliveryEditor /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
